import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { updateMeta, updateLink } from '../utils/seo'

const sections = [
  {
    id: 'getting-started',
    label: 'Getting Started',
    icon: '🚀',
    group: 'Basics'
  },
  {
    id: 'viewer-commands',
    label: 'Viewer Commands',
    icon: '💬',
    group: 'Basics'
  },
  {
    id: 'admin-commands',
    label: 'Admin Commands',
    icon: '🔧',
    group: 'Basics'
  },
  {
    id: '2d-settings',
    label: '2D Settings',
    icon: '🎨',
    group: '2D Configuration'
  },
  {
    id: '2d-visual',
    label: 'Visual & Sprites',
    icon: '✨',
    group: '2D Configuration'
  },
  {
    id: '2d-custom',
    label: 'Custom 2D Critters',
    icon: '🖼️',
    group: '2D Configuration'
  },
  {
    id: '3d-overview',
    label: '3D Overview',
    icon: '🧊',
    group: '3D Configuration'
  },
  {
    id: '3d-upload',
    label: 'Uploading Models',
    icon: '📦',
    group: '3D Configuration'
  },
  {
    id: '3d-animations',
    label: 'Animation Mapping',
    icon: '🎬',
    group: '3D Configuration'
  },
  {
    id: '3d-unity',
    label: 'Unity Export Guide',
    icon: '🎮',
    group: '3D Configuration'
  },
  {
    id: 'universal-movement',
    label: 'Movement',
    icon: '🏃',
    group: 'Universal Settings'
  },
  {
    id: 'universal-features',
    label: 'Feature Toggles',
    icon: '⚡',
    group: 'Universal Settings'
  },
  {
    id: 'universal-permissions',
    label: 'Permissions',
    icon: '🔐',
    group: 'Universal Settings'
  },
  {
    id: 'universal-sleep',
    label: 'Sleep Mode',
    icon: '😴',
    group: 'Universal Settings'
  },
  {
    id: 'obs-setup',
    label: 'OBS Setup',
    icon: '📺',
    group: 'Setup'
  },
  {
    id: 'troubleshooting',
    label: 'Troubleshooting',
    icon: '🔍',
    group: 'Setup'
  }
]

function Guide() {
  const location = useLocation()
  const [activeSection, setActiveSection] = useState('getting-started')
  const [sidebarOpen, setSidebarOpen] = useState(false)

  useEffect(() => {
    window.scrollTo(0, 0)
    document.title = 'Documentation & Guide - Stream Familiars'
    updateMeta('description', 'Complete documentation for Stream Familiars. Setup guide, chat commands, 2D sprites, 3D model configuration, animation mapping, OBS setup, and troubleshooting.')
    updateMeta('og:title', 'Documentation & Guide - Stream Familiars', 'property')
    updateMeta('og:description', 'Full documentation for Stream Familiars Twitch overlay - 2D sprites, 3D models, commands, and settings.', 'property')
    updateMeta('og:url', 'https://streamfamiliars.com/guide', 'property')
    updateLink('canonical', 'https://streamfamiliars.com/guide')
  }, [])

  useEffect(() => {
    const hash = location.hash.replace('#', '')
    if (hash && sections.find(s => s.id === hash)) {
      setActiveSection(hash)
    }
  }, [location.hash])

  useEffect(() => {
    const el = document.getElementById(activeSection)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    setSidebarOpen(false)
  }, [activeSection])

  const groups = [...new Set(sections.map(s => s.group))]

  return (
    <div className="guide-page">
      {/* Top Nav */}
      <nav className="guide-topnav">
        <div className="guide-topnav-inner">
          <div className="guide-topnav-left">
            <button className="guide-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
              <span></span><span></span><span></span>
            </button>
            <Link to="/" className="guide-logo">
              <div className="guide-logo-mark">SF</div>
              <span className="guide-logo-text">Stream Familiars</span>
            </Link>
          </div>
          <div className="guide-topnav-center">
            <span className="guide-topnav-title">Documentation</span>
          </div>
          <div className="guide-topnav-right">
            <Link to="/tutorial" className="guide-topnav-link">Tutorial</Link>
            <Link to="/" className="guide-topnav-link">Home</Link>
          </div>
        </div>
      </nav>

      <div className="guide-layout">
        {/* Sidebar */}
        <aside className={`guide-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="guide-sidebar-inner">
            {groups.map(group => (
              <div key={group} className="guide-nav-group">
                <div className="guide-nav-group-label">{group}</div>
                {sections.filter(s => s.group === group).map(section => (
                  <button
                    key={section.id}
                    className={`guide-nav-item ${activeSection === section.id ? 'active' : ''}`}
                    onClick={() => setActiveSection(section.id)}
                  >
                    <span className="guide-nav-icon">{section.icon}</span>
                    <span>{section.label}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </aside>

        {/* Overlay for mobile sidebar */}
        {sidebarOpen && <div className="guide-sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

        {/* Main Content */}
        <main className="guide-main">
          <div className="guide-content">

            {/* Getting Started */}
            <section id="getting-started" className="guide-section">
              <h1>Getting Started</h1>
              <p className="guide-lead">Stream Familiars adds interactive critter pets to your Twitch stream overlay. Viewers spawn and interact with critters via chat commands.</p>

              <div className="guide-steps">
                <div className="guide-step">
                  <div className="guide-step-num">1</div>
                  <div>
                    <h3>Connect Your Twitch Account</h3>
                    <p>Visit the <Link to="/">homepage</Link> and click "Connect Twitch" to authenticate your broadcaster account.</p>
                  </div>
                </div>
                <div className="guide-step">
                  <div className="guide-step-num">2</div>
                  <div>
                    <h3>Add OBS Browser Source</h3>
                    <p>Copy your overlay URL and add it as a Browser Source in OBS at 1920x1080. See <a href="#obs-setup" onClick={(e) => {e.preventDefault(); setActiveSection('obs-setup')}}>OBS Setup</a> for details.</p>
                  </div>
                </div>
                <div className="guide-step">
                  <div className="guide-step-num">3</div>
                  <div>
                    <h3>Configure Settings</h3>
                    <p>Your overlay shows two buttons: <strong className="accent-2d">2D Settings</strong> and <strong className="accent-3d">3D Settings</strong>. Each panel manages its own critter types independently.</p>
                  </div>
                </div>
                <div className="guide-step">
                  <div className="guide-step-num">4</div>
                  <div>
                    <h3>Go Live</h3>
                    <p>Subscribers type <code>!joincritter</code> in chat to spawn their critter. They can interact using commands like <code>!pet</code>, <code>!feed</code>, <code>!dance</code>, and more.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Viewer Commands */}
            <section id="viewer-commands" className="guide-section">
              <h1>Viewer Commands</h1>
              <p className="guide-lead">Commands your subscribers can use to interact with their critters.</p>

              <table className="guide-table">
                <thead>
                  <tr><th>Command</th><th>Description</th><th>Effect</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>!joincritter [type]</code></td><td>Spawn your critter on the overlay</td><td>-</td></tr>
                  <tr><td><code>!leavecritter</code></td><td>Remove your critter</td><td>-</td></tr>
                  <tr><td><code>!crittertype &lt;type&gt;</code></td><td>Change your critter type</td><td>-</td></tr>
                  <tr><td><code>!critter</code></td><td>Check your current critter type</td><td>-</td></tr>
                  <tr><td><code>!pet</code></td><td>Pet your critter</td><td>Hearts / Bounce</td></tr>
                  <tr><td><code>!feed</code></td><td>Feed your critter</td><td>Food particles / Eat</td></tr>
                  <tr><td><code>!dance</code></td><td>Make your critter dance</td><td>Music notes / Spin</td></tr>
                  <tr><td><code>!trick</code></td><td>Critter does a flip</td><td>Sparkles / Roll</td></tr>
                  <tr><td><code>!hug @user</code></td><td>Hug another viewer's critter</td><td>Hearts on both</td></tr>
                  <tr><td><code>!bonk @user</code></td><td>Playfully bonk another's critter</td><td>Stars / Hit</td></tr>
                </tbody>
              </table>

              <div className="guide-callout">
                <strong>Chat Bubbles</strong>
                <p>Any regular chat message (not starting with !) appears as a speech bubble above the viewer's critter, truncated to 140 characters.</p>
              </div>
            </section>

            {/* Admin Commands */}
            <section id="admin-commands" className="guide-section">
              <h1>Admin Commands</h1>
              <p className="guide-lead">Broadcaster and moderator controls for managing critters on stream.</p>

              <table className="guide-table">
                <thead>
                  <tr><th>Command</th><th>Description</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>!feast</code></td><td>Feed ALL critters at once - food rain effect on every critter</td></tr>
                  <tr><td><code>!addcritter @user [type]</code></td><td>Manually add a critter for a specific viewer</td></tr>
                  <tr><td><code>!removecritter @user</code></td><td>Remove a specific viewer's critter</td></tr>
                  <tr><td><code>!clearcritters</code></td><td>Remove all critters from the overlay</td></tr>
                </tbody>
              </table>

              <div className="guide-callout warning">
                <strong>Permissions</strong>
                <p>Admin commands require mod or broadcaster status by default. Configure in <a href="#universal-permissions" onClick={(e) => {e.preventDefault(); setActiveSection('universal-permissions')}}>Permissions</a> settings.</p>
              </div>
            </section>

            {/* 2D Settings */}
            <section id="2d-settings" className="guide-section">
              <h1 className="accent-2d">2D Settings Panel</h1>
              <p className="guide-lead">The purple <strong className="accent-2d">2D Settings</strong> button on your overlay opens the 2D configuration panel.</p>

              <h2>Tabs Overview</h2>
              <div className="guide-card-grid">
                <div className="guide-card">
                  <h3>Movement</h3>
                  <p>Static mode, roam interval, movement speed, update interval, and screen boundary controls (X/Y min/max).</p>
                </div>
                <div className="guide-card">
                  <h3>Visual</h3>
                  <p>Critter scale, username display, chat bubbles, sprite animations toggle, animation FPS, and effect durations.</p>
                </div>
                <div className="guide-card">
                  <h3>Features</h3>
                  <p>Toggle each command on/off, sleep mode, max critters limit, and default critter type selection (2D types only).</p>
                </div>
                <div className="guide-card">
                  <h3>Permissions</h3>
                  <p>Subscription tier requirements, mod admin access, broadcaster-only mode.</p>
                </div>
                <div className="guide-card">
                  <h3>Admin</h3>
                  <p>Add/remove 2D critters manually, create custom critter types, upload spritesheets. Shows active 2D critters list.</p>
                </div>
                <div className="guide-card">
                  <h3>Help</h3>
                  <p>In-panel quick reference for commands, settings explanations, and OBS setup instructions.</p>
                </div>
              </div>

              <h2>Default 2D Critter Types</h2>
              <div className="guide-tags">
                {['Cat', 'Bunny', 'Owl', 'Slime', 'Fox', 'Penguin', 'Hamster', 'Panda'].map(t => (
                  <span key={t} className="guide-tag tag-2d">{t}</span>
                ))}
              </div>
              <p>Custom 2D types can be created via Admin &gt; "Create Custom Critter" by uploading mouth-open and mouth-closed PNG images.</p>
            </section>

            {/* 2D Visual & Sprites */}
            <section id="2d-visual" className="guide-section">
              <h1 className="accent-2d">Visual & Sprites</h1>
              <p className="guide-lead">Configure how 2D critters look and animate.</p>

              <h2>Static Images (Default)</h2>
              <p>Each 2D critter type uses two PNG images: mouth closed (idle) and mouth open (talking/eating). CSS animations handle breathing, walking sway, and interaction effects.</p>

              <h2>Animated Spritesheets</h2>
              <p>For frame-by-frame animations, upload spritesheets (PNG + JSON metadata) for each animation state:</p>
              <div className="guide-props">
                <div className="guide-prop"><span className="guide-prop-name">Idle</span><span>Default standing animation</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Walk</span><span>Movement animation when roaming</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Eat</span><span>Plays during !feed command</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Pet</span><span>Plays during !pet command</span></div>
              </div>
              <p>Enable in 2D Settings &gt; Visual &gt; "Enable Animated Sprites". Use TexturePacker JSON (Array) format.</p>

              <h2>Animation Durations</h2>
              <div className="guide-props">
                <div className="guide-prop"><span className="guide-prop-name">Feed Duration</span><span>500ms - 5000ms (default 2000ms)</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Pet Duration</span><span>250ms - 2000ms (default 500ms)</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Dance Duration</span><span>Default 3000ms</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Trick Duration</span><span>Default 1500ms</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Hug Duration</span><span>Default 2500ms</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Bonk Duration</span><span>Default 1500ms</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Chat Bubble</span><span>2000ms - 15000ms (default 5000ms)</span></div>
              </div>
            </section>

            {/* Custom 2D Critters */}
            <section id="2d-custom" className="guide-section">
              <h1 className="accent-2d">Custom 2D Critters</h1>
              <p className="guide-lead">Create your own critter types with custom artwork.</p>

              <h2>Creating a Custom Type</h2>
              <ol className="guide-ol">
                <li>Open <strong className="accent-2d">2D Settings</strong> &gt; Admin tab</li>
                <li>Click "Create Custom Critter"</li>
                <li>Enter a name and type key (lowercase, alphanumeric + underscores)</li>
                <li>Upload <strong>Mouth Closed</strong> PNG (required)</li>
                <li>Upload <strong>Mouth Open</strong> PNG (required)</li>
                <li>Optionally upload a <strong>Smiling</strong> PNG</li>
                <li>Click Create - viewers can now use <code>!joincritter yourtype</code></li>
              </ol>

              <div className="guide-callout">
                <strong>Image Tips</strong>
                <p>Use transparent PNG files. Recommended size: 128x128 to 256x256 pixels. Keep file size under 5MB.</p>
              </div>
            </section>

            {/* 3D Overview */}
            <section id="3d-overview" className="guide-section">
              <h1 className="accent-3d">3D Models Overview</h1>
              <p className="guide-lead">The green <strong className="accent-3d">3D Settings</strong> button opens the 3D configuration panel. Upload GLB models for fully animated 3D critters.</p>

              <h2>3D Settings Tabs</h2>
              <div className="guide-card-grid">
                <div className="guide-card card-3d">
                  <h3>Settings</h3>
                  <p>Enable/disable 3D rendering globally, adjust 3D model scale, and see which types have models uploaded vs pending.</p>
                </div>
                <div className="guide-card card-3d">
                  <h3>Upload Models</h3>
                  <p>Select a 3D critter type, upload a GLB file, map animations to behaviors, set per-model scale.</p>
                </div>
                <div className="guide-card card-3d">
                  <h3>Model Library</h3>
                  <p>View all uploaded 3D models with animation counts and scale info. Delete models you no longer need.</p>
                </div>
                <div className="guide-card card-3d">
                  <h3>Admin</h3>
                  <p>Add/remove 3D critters manually. Shows only active 3D critters in the list.</p>
                </div>
              </div>

              <h2>Available 3D Critter Types (27)</h2>
              <h3>Farm</h3>
              <div className="guide-tags">
                {['Buffalo', 'Chick', 'Cow', 'Donkey', 'Duck', 'Hen', 'Pig', 'Rooster', 'Sheep'].map(t => (
                  <span key={t} className="guide-tag tag-3d">{t}</span>
                ))}
              </div>
              <h3>Wild</h3>
              <div className="guide-tags">
                {['Crow', 'Eagle', 'Fox', 'Hog', 'Hornbill', 'Owl', 'Raccoon', 'Snake', 'Wolf'].map(t => (
                  <span key={t} className="guide-tag tag-3d">{t}</span>
                ))}
              </div>
              <h3>Domestic</h3>
              <div className="guide-tags">
                {['Cat', 'Dog', 'Dove', 'Goldfish', 'Mice', 'Parrot', 'Pigeon', 'Rabbit', 'Tortoise'].map(t => (
                  <span key={t} className="guide-tag tag-3d">{t}</span>
                ))}
              </div>
            </section>

            {/* Uploading Models */}
            <section id="3d-upload" className="guide-section">
              <h1 className="accent-3d">Uploading 3D Models</h1>
              <p className="guide-lead">Step-by-step guide to adding 3D models to your critter types.</p>

              <ol className="guide-ol">
                <li>Click <strong className="accent-3d">3D Settings</strong> on your overlay</li>
                <li>Go to the <strong>Upload Models</strong> tab</li>
                <li>Select a critter type from the dropdown (only 3D types shown)</li>
                <li>Set the <strong>Model Scale</strong> (0.5 - 20x, default 5.0)</li>
                <li>Click the upload zone and select your <strong>.glb</strong> file</li>
                <li>Animations are auto-detected and mapped by name</li>
                <li>Review and adjust the animation mapping if needed</li>
                <li>Click <strong>Upload 3D Model</strong></li>
              </ol>

              <div className="guide-callout warning">
                <strong>GLB Format Required</strong>
                <p>Only <strong>.glb</strong> (binary GLTF) files are accepted. GLTF text format (.gltf + .bin) is not supported because the separate .bin file cannot be uploaded. See <a href="#3d-unity" onClick={(e) => {e.preventDefault(); setActiveSection('3d-unity')}}>Unity Export Guide</a> for how to export correctly.</p>
              </div>

              <h2>Model Requirements</h2>
              <div className="guide-props">
                <div className="guide-prop"><span className="guide-prop-name">Format</span><span>GLB (binary GLTF) only</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Max File Size</span><span>50MB</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Recommended Polygons</span><span>Under 10K triangles per model</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Recommended File Size</span><span>Under 10MB for smooth performance</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Textures</span><span>Embedded in GLB (auto-included)</span></div>
              </div>
            </section>

            {/* Animation Mapping */}
            <section id="3d-animations" className="guide-section">
              <h1 className="accent-3d">Animation Mapping</h1>
              <p className="guide-lead">Map your 3D model's animation clips to critter behaviors. The system supports 14 animation states.</p>

              <table className="guide-table">
                <thead>
                  <tr><th>State</th><th>Auto-Mapped From</th><th>Triggered By</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>Idle A</code></td><td>Idle_A, Idle A</td><td>Auto-cycles every 5s</td></tr>
                  <tr><td><code>Idle B</code></td><td>Idle_B, Idle B</td><td>Auto-cycles every 5s</td></tr>
                  <tr><td><code>Idle C</code></td><td>Idle_C, Idle C</td><td>Auto-cycles every 5s</td></tr>
                  <tr><td><code>Walk</code></td><td>Walk</td><td>Roaming movement</td></tr>
                  <tr><td><code>Run</code></td><td>Run</td><td>Walk fallback</td></tr>
                  <tr><td><code>Eat</code></td><td>Eat, Feed</td><td><code>!feed</code> / <code>!feast</code></td></tr>
                  <tr><td><code>Pet</code></td><td>Bounce, Pet, Pat, Happy</td><td><code>!pet</code></td></tr>
                  <tr><td><code>Dance</code></td><td>Spin, Dance, Splash</td><td><code>!dance</code></td></tr>
                  <tr><td><code>Trick</code></td><td>Roll, Trick, Flip</td><td><code>!trick</code></td></tr>
                  <tr><td><code>Hug</code></td><td>Sit, Hug, Love</td><td><code>!hug</code></td></tr>
                  <tr><td><code>Bonk</code></td><td>Hit, Bonk, Attack</td><td><code>!bonk</code></td></tr>
                  <tr><td><code>Sleep</code></td><td>Sit, Sleep, Rest</td><td>Inactive timeout</td></tr>
                  <tr><td><code>Spawn</code></td><td>Jump, Spawn, Appear</td><td>Critter appears</td></tr>
                  <tr><td><code>Despawn</code></td><td>Death, Despawn, Die</td><td>Critter removed</td></tr>
                </tbody>
              </table>

              <h2>Fallback Chains</h2>
              <p>If a model is missing a specific animation, the system falls back to alternatives:</p>
              <div className="guide-props">
                <div className="guide-prop"><span className="guide-prop-name">Dance</span><span>Spin &rarr; first idle</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Trick</span><span>Roll &rarr; Jump &rarr; first idle</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Hug</span><span>Sit &rarr; Bounce &rarr; first idle</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Bonk</span><span>Hit &rarr; Attack &rarr; first idle</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Walk</span><span>Run &rarr; first idle</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Despawn</span><span>Death &rarr; first idle</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Sleep</span><span>Sit &rarr; first idle</span></div>
              </div>

              <h2>Idle Cycling</h2>
              <p>If your model has Idle_A, Idle_B, and Idle_C animations, the system automatically rotates between them every 5 seconds when the critter is in its resting state. Models with only one idle animation will use that exclusively.</p>
            </section>

            {/* Unity Export */}
            <section id="3d-unity" className="guide-section">
              <h1 className="accent-3d">Unity Export Guide</h1>
              <p className="guide-lead">How to export 3D models from Unity with animations as GLB files.</p>

              <h2>Option 1: UnityGLTF (Recommended)</h2>
              <ol className="guide-ol">
                <li>Open Window &gt; Package Manager &gt; + &gt; Add package from git URL</li>
                <li>Enter: <code>https://github.com/KhronosGroup/UnityGLTF.git</code></li>
                <li>Select your model in the scene</li>
                <li>Right-click &gt; <strong>Export GLB</strong></li>
                <li>In export settings, enable <strong>Export Animations</strong> and <strong>Export All Animation Clips</strong></li>
                <li>Save as <code>.glb</code></li>
              </ol>

              <h2>Option 2: GLTFast</h2>
              <ol className="guide-ol">
                <li>Install via Package Manager: <code>com.unity.cloud.gltfast</code></li>
                <li>Use the export API to write GLB files with embedded animations</li>
              </ol>

              <h2>Converting Existing Files</h2>
              <p>If you already have <code>.gltf</code> + <code>.bin</code> files, convert to GLB:</p>
              <div className="guide-props">
                <div className="guide-prop"><span className="guide-prop-name">Online Tool</span><span><strong>gltf.report</strong> - drag & drop, download as GLB</span></div>
                <div className="guide-prop"><span className="guide-prop-name">CLI Tool</span><span><code>npx gltf-pipeline -i model.gltf -o model.glb</code></span></div>
                <div className="guide-prop"><span className="guide-prop-name">Blender</span><span>File &gt; Import GLTF, then File &gt; Export &gt; GLTF (.glb)</span></div>
              </div>

              <div className="guide-callout">
                <strong>Animation Naming</strong>
                <p>Name your animation clips clearly in Unity (e.g., Idle_A, Walk, Eat, Bounce, Hit) so they auto-map correctly when uploaded. The system matches clip names using pattern matching.</p>
              </div>
            </section>

            {/* Universal: Movement */}
            <section id="universal-movement" className="guide-section">
              <h1>Movement Settings</h1>
              <p className="guide-lead">Controls how critters move around the screen. Applies to both 2D and 3D critters.</p>

              <div className="guide-props">
                <div className="guide-prop"><span className="guide-prop-name">Static Mode</span><span>Critters spawn at random positions but don't move. Animations still play.</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Roam Interval</span><span>1000ms - 10000ms. How often critters pick a new destination. Default: 3000ms.</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Movement Speed</span><span>0.05 - 0.5. Speed as % of screen per update tick. Default: 0.15.</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Update Interval</span><span>50ms - 500ms. Position update frequency. Lower = smoother but more CPU. Default: 250ms.</span></div>
              </div>

              <h2>Screen Boundaries</h2>
              <p>Define the area critters can roam using percentage-based coordinates:</p>
              <div className="guide-props">
                <div className="guide-prop"><span className="guide-prop-name">Min X / Max X</span><span>Horizontal bounds (default 5% - 85%)</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Min Y / Max Y</span><span>Vertical bounds (default 0% - 15%)</span></div>
              </div>
              <p>Useful for restricting critters to specific parts of the screen, like the bottom bar area.</p>
            </section>

            {/* Universal: Feature Toggles */}
            <section id="universal-features" className="guide-section">
              <h1>Feature Toggles</h1>
              <p className="guide-lead">Enable or disable individual commands and features. Found in 2D Settings &gt; Features tab.</p>

              <div className="guide-props">
                <div className="guide-prop"><span className="guide-prop-name">Allow !feed</span><span>Toggle the feed command on/off</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Allow !pet</span><span>Toggle the pet command on/off</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Allow !dance</span><span>Toggle the dance command on/off</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Allow !trick</span><span>Toggle the trick command on/off</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Allow !hug</span><span>Toggle the hug command on/off</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Allow !bonk</span><span>Toggle the bonk command on/off</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Allow !crittertype</span><span>Let viewers change their critter type</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Chat Bubbles</span><span>Show/hide speech bubbles above critters</span></div>
              </div>

              <h2>Critter Limits</h2>
              <div className="guide-props">
                <div className="guide-prop"><span className="guide-prop-name">Max Critters</span><span>5 - 50. Oldest critters removed when limit reached. Default: 20.</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Default Critter Type</span><span>Type assigned to new viewers who join without specifying.</span></div>
              </div>
            </section>

            {/* Universal: Permissions */}
            <section id="universal-permissions" className="guide-section">
              <h1>Permissions</h1>
              <p className="guide-lead">Control who can spawn critters and use admin commands.</p>

              <h2>Subscription Requirements</h2>
              <div className="guide-props">
                <div className="guide-prop"><span className="guide-prop-name">Require Subscription</span><span>When enabled, only subscribers can create critters</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Minimum Tier</span><span>Tier 1, 2, or 3. Default: Tier 3.</span></div>
              </div>

              <h2>Admin Access</h2>
              <div className="guide-props">
                <div className="guide-prop"><span className="guide-prop-name">Mods Can Use Admin Commands</span><span>Allow moderators to add/remove critters. Default: enabled.</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Broadcaster-Only Admin</span><span>Restrict all admin commands to the broadcaster only.</span></div>
              </div>
            </section>

            {/* Universal: Sleep Mode */}
            <section id="universal-sleep" className="guide-section">
              <h1>Sleep Mode</h1>
              <p className="guide-lead">Inactive viewers' critters automatically fall asleep after a configurable timeout.</p>

              <h2>How It Works</h2>
              <ul className="guide-ul">
                <li>When a viewer hasn't chatted for the configured timeout, their critter falls asleep</li>
                <li>Sleeping critters become greyed out and stop moving</li>
                <li>A floating "Zzz" indicator appears above sleeping critters</li>
                <li>3D critters play their Sleep animation (falls back to Sit)</li>
                <li>When the viewer chats again, the critter wakes up instantly</li>
              </ul>

              <div className="guide-props">
                <div className="guide-prop"><span className="guide-prop-name">Enable Sleep Mode</span><span>Toggle on/off. Default: disabled.</span></div>
                <div className="guide-prop"><span className="guide-prop-name">Sleep Timeout</span><span>1 - 30 minutes. How long before inactive critters sleep. Default: 5 min.</span></div>
              </div>
            </section>

            {/* OBS Setup */}
            <section id="obs-setup" className="guide-section">
              <h1>OBS Setup</h1>
              <p className="guide-lead">Add Stream Familiars as a browser source in OBS Studio.</p>

              <ol className="guide-ol">
                <li>Open OBS Studio</li>
                <li>In your scene, click <strong>+</strong> under Sources</li>
                <li>Select <strong>Browser</strong></li>
                <li>Name it "Stream Familiars" and click OK</li>
                <li>Set the URL to your overlay URL:
                  <div className="guide-code-block">
                    <code>https://yourdomain.com/overlay/your_twitch_username</code>
                  </div>
                </li>
                <li>Set <strong>Width</strong> to 1920 and <strong>Height</strong> to 1080 (match your canvas)</li>
                <li>Leave Custom CSS empty</li>
                <li>Check <strong>"Shutdown source when not visible"</strong> for performance</li>
                <li>Click OK</li>
              </ol>

              <div className="guide-callout">
                <strong>Positioning</strong>
                <p>The overlay is transparent, so position the browser source to cover your entire canvas. Critters respect the boundary settings you configure, so they'll stay within your defined area.</p>
              </div>
            </section>

            {/* Troubleshooting */}
            <section id="troubleshooting" className="guide-section">
              <h1>Troubleshooting</h1>
              <p className="guide-lead">Common issues and how to fix them.</p>

              <div className="guide-faq">
                <div className="guide-faq-item">
                  <h3>3D model shows error / doesn't load</h3>
                  <p>Make sure you're uploading a <strong>.glb</strong> file, not .gltf. GLTF text format references external .bin files that can't be uploaded separately. Re-export as GLB from your 3D tool or convert using <code>npx gltf-pipeline -i model.gltf -o model.glb</code>.</p>
                </div>
                <div className="guide-faq-item">
                  <h3>Critter doesn't appear after !joincritter</h3>
                  <p>Check that: (1) the viewer meets the subscription tier requirement, (2) the max critters limit hasn't been reached, (3) the overlay is connected (check the status indicator).</p>
                </div>
                <div className="guide-faq-item">
                  <h3>3D model is too big/small</h3>
                  <p>Adjust the per-model scale in 3D Settings &gt; Upload Models (0.5 - 20x). You can also adjust the global 3D scale in 3D Settings &gt; Settings tab.</p>
                </div>
                <div className="guide-faq-item">
                  <h3>Animations don't play</h3>
                  <p>Check the animation mapping in 3D Settings &gt; Upload Models. Make sure clips are mapped to the correct states. If clips weren't auto-detected, your GLB may not contain embedded animations - re-export with "Export Animations" enabled.</p>
                </div>
                <div className="guide-faq-item">
                  <h3>Overlay is slow / laggy</h3>
                  <p>Reduce model polygon count (aim for under 10K triangles). Lower the max critters setting. Enable "Shutdown source when not visible" in OBS. Keep GLB files under 10MB.</p>
                </div>
              </div>
            </section>

          </div>
        </main>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        .guide-page {
          min-height: 100vh;
          background: #0a0a0b;
          color: #e4e4e7;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        body { overflow: auto !important; }

        .accent-2d { color: #a78bfa; }
        .accent-3d { color: #10b981; }

        /* Top Nav */
        .guide-topnav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          height: 56px;
          background: rgba(10, 10, 11, 0.95);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255,255,255,0.06);
          z-index: 200;
        }

        .guide-topnav-inner {
          max-width: 1400px;
          margin: 0 auto;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 24px;
        }

        .guide-topnav-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .guide-menu-btn {
          display: none;
          flex-direction: column;
          gap: 4px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
        }

        .guide-menu-btn span {
          width: 20px;
          height: 2px;
          background: #a1a1aa;
          border-radius: 1px;
        }

        .guide-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
          color: inherit;
        }

        .guide-logo-mark {
          width: 32px;
          height: 32px;
          background: #a78bfa;
          color: #0a0a0b;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 12px;
        }

        .guide-logo-text {
          font-weight: 600;
          font-size: 15px;
          color: #fafafa;
        }

        .guide-topnav-center {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
        }

        .guide-topnav-title {
          font-size: 13px;
          font-weight: 600;
          color: #71717a;
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }

        .guide-topnav-right {
          display: flex;
          gap: 8px;
        }

        .guide-topnav-link {
          color: #a1a1aa;
          text-decoration: none;
          font-size: 13px;
          font-weight: 500;
          padding: 6px 14px;
          border-radius: 8px;
          transition: all 0.2s;
        }

        .guide-topnav-link:hover {
          color: #fafafa;
          background: rgba(255,255,255,0.06);
        }

        /* Layout */
        .guide-layout {
          display: flex;
          padding-top: 56px;
          min-height: calc(100vh - 56px);
        }

        /* Sidebar */
        .guide-sidebar {
          width: 260px;
          flex-shrink: 0;
          position: fixed;
          top: 56px;
          left: 0;
          bottom: 0;
          overflow-y: auto;
          border-right: 1px solid rgba(255,255,255,0.06);
          background: #0c0c0d;
          z-index: 150;
        }

        .guide-sidebar-inner {
          padding: 20px 12px;
        }

        .guide-sidebar-overlay {
          display: none;
        }

        .guide-nav-group {
          margin-bottom: 24px;
        }

        .guide-nav-group-label {
          font-size: 11px;
          font-weight: 700;
          color: #52525b;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          padding: 0 12px;
          margin-bottom: 6px;
        }

        .guide-nav-item {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 8px 12px;
          border: none;
          background: none;
          color: #a1a1aa;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          border-radius: 8px;
          transition: all 0.15s;
          text-align: left;
          font-family: inherit;
        }

        .guide-nav-item:hover {
          color: #fafafa;
          background: rgba(255,255,255,0.04);
        }

        .guide-nav-item.active {
          color: #fafafa;
          background: rgba(167, 139, 250, 0.1);
          font-weight: 600;
        }

        .guide-nav-icon {
          font-size: 15px;
          width: 20px;
          text-align: center;
        }

        /* Main Content */
        .guide-main {
          flex: 1;
          margin-left: 260px;
          min-width: 0;
        }

        .guide-content {
          max-width: 820px;
          margin: 0 auto;
          padding: 40px 48px 80px;
        }

        .guide-section {
          padding-top: 24px;
          padding-bottom: 48px;
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }

        .guide-section:last-child {
          border-bottom: none;
        }

        .guide-section h1 {
          font-size: 28px;
          font-weight: 700;
          color: #fafafa;
          margin-bottom: 8px;
          letter-spacing: -0.02em;
        }

        .guide-section h2 {
          font-size: 20px;
          font-weight: 600;
          color: #fafafa;
          margin: 32px 0 16px;
          letter-spacing: -0.01em;
        }

        .guide-section h3 {
          font-size: 15px;
          font-weight: 600;
          color: #d4d4d8;
          margin: 20px 0 8px;
        }

        .guide-lead {
          font-size: 16px;
          color: #71717a;
          line-height: 1.7;
          margin-bottom: 28px;
        }

        .guide-section p {
          font-size: 14px;
          color: #a1a1aa;
          line-height: 1.7;
          margin-bottom: 16px;
        }

        .guide-section code {
          background: rgba(167, 139, 250, 0.1);
          color: #c4b5fd;
          padding: 2px 7px;
          border-radius: 4px;
          font-size: 13px;
          font-family: 'Consolas', 'Monaco', monospace;
        }

        .guide-section a {
          color: #a78bfa;
          text-decoration: none;
        }

        .guide-section a:hover {
          text-decoration: underline;
        }

        /* Steps */
        .guide-steps {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .guide-step {
          display: flex;
          gap: 16px;
          background: #141416;
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 12px;
          padding: 20px;
        }

        .guide-step-num {
          width: 36px;
          height: 36px;
          background: rgba(167, 139, 250, 0.1);
          color: #a78bfa;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 15px;
          flex-shrink: 0;
        }

        .guide-step h3 {
          margin: 0 0 4px;
          color: #fafafa;
        }

        .guide-step p {
          margin: 0;
        }

        /* Table */
        .guide-table {
          width: 100%;
          border-collapse: collapse;
          margin: 16px 0;
          font-size: 14px;
        }

        .guide-table th {
          text-align: left;
          padding: 10px 14px;
          background: rgba(255,255,255,0.03);
          color: #71717a;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }

        .guide-table td {
          padding: 10px 14px;
          color: #a1a1aa;
          border-bottom: 1px solid rgba(255,255,255,0.03);
        }

        .guide-table tr:last-child td {
          border-bottom: none;
        }

        .guide-table code {
          font-size: 13px;
        }

        /* Callout */
        .guide-callout {
          background: rgba(167, 139, 250, 0.05);
          border: 1px solid rgba(167, 139, 250, 0.12);
          border-radius: 12px;
          padding: 18px 22px;
          margin: 20px 0;
        }

        .guide-callout.warning {
          background: rgba(249, 115, 22, 0.05);
          border-color: rgba(249, 115, 22, 0.12);
        }

        .guide-callout strong {
          display: block;
          color: #fafafa;
          font-size: 14px;
          margin-bottom: 6px;
        }

        .guide-callout p {
          margin: 0;
          font-size: 13px;
          color: #71717a;
          line-height: 1.6;
        }

        /* Card Grid */
        .guide-card-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 14px;
          margin: 16px 0;
        }

        .guide-card {
          background: #141416;
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 12px;
          padding: 20px;
        }

        .guide-card.card-3d {
          border-top: 2px solid #10b981;
        }

        .guide-card h3 {
          font-size: 15px;
          color: #fafafa;
          margin: 0 0 8px;
        }

        .guide-card p {
          font-size: 13px;
          color: #71717a;
          margin: 0;
        }

        /* Tags */
        .guide-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin: 10px 0 16px;
        }

        .guide-tag {
          padding: 4px 12px;
          border-radius: 100px;
          font-size: 12px;
          font-weight: 600;
        }

        .tag-2d {
          background: rgba(167, 139, 250, 0.1);
          color: #a78bfa;
        }

        .tag-3d {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        /* Props List */
        .guide-props {
          display: flex;
          flex-direction: column;
          margin: 12px 0 20px;
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 10px;
          overflow: hidden;
        }

        .guide-prop {
          display: flex;
          gap: 16px;
          padding: 10px 16px;
          border-bottom: 1px solid rgba(255,255,255,0.03);
          font-size: 13px;
        }

        .guide-prop:last-child {
          border-bottom: none;
        }

        .guide-prop-name {
          min-width: 160px;
          color: #d4d4d8;
          font-weight: 600;
          flex-shrink: 0;
        }

        .guide-prop span:last-child {
          color: #71717a;
        }

        /* Ordered / Unordered List */
        .guide-ol, .guide-ul {
          padding-left: 20px;
          margin: 12px 0;
        }

        .guide-ol li, .guide-ul li {
          font-size: 14px;
          color: #a1a1aa;
          line-height: 1.7;
          margin-bottom: 6px;
        }

        .guide-ol li strong, .guide-ul li strong {
          color: #d4d4d8;
        }

        /* Code Block */
        .guide-code-block {
          background: #141416;
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 8px;
          padding: 12px 16px;
          margin: 8px 0;
          overflow-x: auto;
        }

        .guide-code-block code {
          background: none;
          padding: 0;
          color: #c4b5fd;
          font-size: 13px;
        }

        /* FAQ */
        .guide-faq {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .guide-faq-item {
          background: #141416;
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 12px;
          padding: 20px 24px;
        }

        .guide-faq-item h3 {
          font-size: 15px;
          color: #fafafa;
          margin: 0 0 8px;
        }

        .guide-faq-item p {
          font-size: 13px;
          color: #71717a;
          line-height: 1.6;
          margin: 0;
        }

        /* Scrollbar */
        .guide-sidebar::-webkit-scrollbar {
          width: 6px;
        }

        .guide-sidebar::-webkit-scrollbar-track {
          background: transparent;
        }

        .guide-sidebar::-webkit-scrollbar-thumb {
          background: #27272a;
          border-radius: 3px;
        }

        /* Mobile */
        @media (max-width: 768px) {
          .guide-menu-btn {
            display: flex;
          }

          .guide-topnav-center {
            display: none;
          }

          .guide-sidebar {
            transform: translateX(-100%);
            transition: transform 0.3s ease;
          }

          .guide-sidebar.open {
            transform: translateX(0);
          }

          .guide-sidebar-overlay {
            display: block;
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.5);
            z-index: 140;
          }

          .guide-main {
            margin-left: 0;
          }

          .guide-content {
            padding: 24px 20px 60px;
          }

          .guide-section h1 {
            font-size: 24px;
          }

          .guide-card-grid {
            grid-template-columns: 1fr;
          }

          .guide-prop {
            flex-direction: column;
            gap: 2px;
          }

          .guide-prop-name {
            min-width: auto;
          }

          .guide-table {
            font-size: 13px;
          }

          .guide-table th, .guide-table td {
            padding: 8px 10px;
          }
        }
      `}</style>
    </div>
  )
}

export default Guide
